import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { MasteryProvider } from "./context/MasteryContext";
import { InstrumentProvider } from "./context/InstrumentContext";
import { LanguageProvider } from "./context/LanguageContext";
import Layout from "./components/Layout";
import Celebration from "./components/Celebration";

import { LoginPage, RegisterTeacherPage, RegisterSoloStudentPage, RegisterChoosePage, StudentJoinPage, ForgotPasswordPage, ResetPasswordPage } from "./pages/Auth";
import { SubscribePage, SubscribeSuccessPage } from "./pages/Subscribe";
import TeacherDashboard from "./pages/TeacherDashboard";
import { StudentsListPage, StudentFormPage, StudentDetailPage } from "./pages/Students";
import CalendarPage from "./pages/Calendar";
import SongsPage from "./pages/Songs";
import ScalesPage from "./pages/Scales";
import MyPianoPage from "./pages/MyPiano";
import ResourceHubPage from "./pages/ResourceHub";
import RecitalsPage from "./pages/Recitals";
import InvoicesPage from "./pages/Invoices";
import DevDashboardPage from "./pages/DevDashboard";
import StudentDashboard from "./pages/StudentDashboard";
import FindTeacherPage from "./pages/FindTeacher";
import StudentCurriculumPage from "./pages/StudentCurriculum";
import TeacherCurriculumPage from "./pages/TeacherCurriculum";
import ReferralsPage from "./pages/Referrals";

function LoadingScreen() {
  return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--app-bg)" }}>
    <div className="text-center">
      <div className="text-4xl animate-pulse">🎹</div>
      <div className="mt-2 text-sm" style={{ color: "var(--app-text-muted)" }}>Loading studio…</div>
    </div>
  </div>;
}

function Protected({ children, allow }) {
  const { user, loading, subscription } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (allow && !allow.includes(user.role)) {
    if (user.role === "student") return <Navigate to="/student" replace />;
    return <Navigate to="/teacher" replace />;
  }
  // subscription gate for teachers (not dev, not student)
  if (user.role === "teacher" && !subscription?.active && !location.pathname.startsWith("/subscribe")) {
    return <Navigate to="/subscribe" replace />;
  }
  // solo learners also need active subscription after trial
  if (user.role === "student" && user.solo_learner && !subscription?.active && !location.pathname.startsWith("/subscribe")) {
    return <Navigate to="/subscribe" replace />;
  }
  return children;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "student") return <Navigate to="/student" replace />;
  return <Navigate to="/teacher" replace />;
}

function LayoutRoute({ children }) {
  return <Layout>{children}</Layout>;
}

function ThemedApp() {
  const { user } = useAuth();
  return (
    <ThemeProvider userTheme={user?.premium_theme || user?.theme} userPremium={!!user?.premium} userBg={user?.bg_image_data_url}>
      <LanguageProvider>
      <InstrumentProvider>
        <MasteryProvider>
          <Toaster richColors closeButton position="top-right" />
          {user && <Celebration />}
          <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/register" element={<RegisterChoosePage />} />
        <Route path="/register/teacher" element={<RegisterTeacherPage />} />
        <Route path="/register/solo" element={<RegisterSoloStudentPage />} />
        <Route path="/join" element={<StudentJoinPage />} />
        <Route path="/join/:code" element={<StudentJoinPage />} />

        <Route path="/" element={<RoleRedirect />} />

        <Route path="/subscribe" element={
          <Protected allow={["teacher", "developer", "student"]}>
            <LayoutRoute><SubscribePage /></LayoutRoute>
          </Protected>
        } />
        <Route path="/subscribe/success" element={
          <Protected allow={["teacher", "developer", "student"]}>
            <LayoutRoute><SubscribeSuccessPage /></LayoutRoute>
          </Protected>
        } />

        {/* Teacher */}
        <Route path="/teacher" element={<Protected allow={["teacher", "developer"]}><LayoutRoute><TeacherDashboard /></LayoutRoute></Protected>} />
        <Route path="/teacher/students" element={<Protected allow={["teacher", "developer"]}><LayoutRoute><StudentsListPage /></LayoutRoute></Protected>} />
        <Route path="/teacher/students/new" element={<Protected allow={["teacher", "developer"]}><LayoutRoute><StudentFormPage /></LayoutRoute></Protected>} />
        <Route path="/teacher/students/:id" element={<Protected allow={["teacher", "developer"]}><LayoutRoute><StudentDetailPage /></LayoutRoute></Protected>} />
        <Route path="/teacher/students/:id/edit" element={<Protected allow={["teacher", "developer"]}><LayoutRoute><StudentFormPage /></LayoutRoute></Protected>} />
        <Route path="/teacher/calendar" element={<Protected allow={["teacher", "developer"]}><LayoutRoute><CalendarPage /></LayoutRoute></Protected>} />
        <Route path="/teacher/resource-hub" element={<Protected allow={["teacher", "developer"]}><LayoutRoute><ResourceHubPage /></LayoutRoute></Protected>} />
        <Route path="/teacher/curriculum"  element={<Protected allow={["teacher", "developer"]}><LayoutRoute><TeacherCurriculumPage /></LayoutRoute></Protected>} />
        <Route path="/teacher/recitals" element={<Protected allow={["teacher", "developer"]}><LayoutRoute><RecitalsPage /></LayoutRoute></Protected>} />
        <Route path="/teacher/invoices" element={<Protected allow={["teacher", "developer"]}><LayoutRoute><InvoicesPage /></LayoutRoute></Protected>} />
        <Route path="/teacher/my-music" element={<Protected allow={["teacher", "developer"]}><LayoutRoute><SongsPage scope="teacher" /></LayoutRoute></Protected>} />
        <Route path="/teacher/my-scales" element={<Protected allow={["teacher", "developer"]}><LayoutRoute><ScalesPage /></LayoutRoute></Protected>} />
        <Route path="/teacher/my-piano" element={<Protected allow={["teacher", "developer"]}><LayoutRoute><MyPianoPage /></LayoutRoute></Protected>} />

        {/* Developer */}
        <Route path="/developer" element={<Protected allow={["developer"]}><LayoutRoute><DevDashboardPage /></LayoutRoute></Protected>} />

        {/* Student */}
        <Route path="/student" element={<Protected allow={["student"]}><LayoutRoute><StudentDashboard /></LayoutRoute></Protected>} />
        <Route path="/student/calendar" element={<Protected allow={["student"]}><LayoutRoute><CalendarPage /></LayoutRoute></Protected>} />
        <Route path="/student/songs" element={<Protected allow={["student"]}><LayoutRoute><SongsPage scope="student" /></LayoutRoute></Protected>} />
        <Route path="/student/scales" element={<Protected allow={["student"]}><LayoutRoute><ScalesPage /></LayoutRoute></Protected>} />
        <Route path="/student/my-piano" element={<Protected allow={["student"]}><LayoutRoute><MyPianoPage /></LayoutRoute></Protected>} />
        <Route path="/student/find-teacher" element={<Protected allow={["student"]}><LayoutRoute><FindTeacherPage /></LayoutRoute></Protected>} />
        <Route path="/student/curriculum" element={<Protected allow={["student"]}><LayoutRoute><StudentCurriculumPage /></LayoutRoute></Protected>} />

        {/* Referrals — teachers, developer, and solo learners */}
        <Route path="/referrals" element={
          <Protected allow={["teacher", "developer", "student"]}>
            <LayoutRoute><ReferralsPage /></LayoutRoute>
          </Protected>
        } />

        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </MasteryProvider>
      </InstrumentProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ThemedApp />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
